import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpResponse,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CDN_URL, OPEN_AI, OpenAI } from '@maximizer/core/shared/domain';
import {
  Tiktoken,
  TiktokenBPE,
  TiktokenModel,
  getEncodingNameForModel,
} from 'js-tiktoken/lite';
import { Observable, catchError, map, switchMap, tap, throwError } from 'rxjs';
import { ContextService } from '../context.service';
import { parseServerSentEvents } from './sse-parser';

declare const LOAD_UID: string;

@Injectable()
export class OpenAiService {
  private readonly completionsUrl: string;
  private readonly agentsConversationsUrl: string;
  private readonly agentsConversationsClearHistoryUrl: string;
  readonly maxTokensByModel: Partial<Record<TiktokenModel, number>> = {
    'gpt-3.5-turbo': 16385,
    'gpt-4': 128000,
    'gpt-4o': 128000,
    'gpt-4o-mini': 128000,
    'gpt-4.1': 1047576,
    'gpt-4.1-mini': 1047576,
    'gpt-4.1-nano': 1047576,
  };

  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
    @Inject(CDN_URL) public cdn: string,
    @Inject(OPEN_AI) public openAi: string,
  ) {
    this.completionsUrl = `${this.openAi}/chat/completions`;
    this.agentsConversationsUrl = `${this.openAi}/agents/conversations`;
    this.agentsConversationsClearHistoryUrl = `${this.openAi}/agents/conversations/clear-history`;
  }

  getSmartQuestions(
    message: OpenAI.GPTMessage[],
    maxTokens: number,
    temperature: number,
  ): Observable<string> {
    const headers = this.getHeaders(false);
    const request: OpenAI.CompletionRequest = {
      messages: message,
      model: 'gpt-4o',
      max_tokens: maxTokens,
      temperature: temperature,
    };

    return this.http
      .post<OpenAI.CompletionResponse>(this.completionsUrl, request, {
        headers: headers,
      })
      .pipe(
        map(
          (response: OpenAI.CompletionResponse) =>
            response.choices[0]?.message?.content ?? '',
        ),
      );
  }

  summarize<T>(
    context: string[],
    texts: string[],
    prompt: string,
    configuration: OpenAI.RequestConfiguration,
    tracker?: OpenAI.RequestTracker,
    options?: OpenAI.RequestOptions,
  ): Observable<T | null> {
    const contextMessages: OpenAI.GPTMessage[] = context.map((text) => ({
      role: 'user',
      content: text,
    }));

    const userMessages: OpenAI.GPTMessage[] = texts.map((text) => ({
      role: 'user',
      content: text,
    }));

    const systemMessage: OpenAI.GPTMessage = {
      role: 'system',
      content: prompt,
    };

    return this.request(
      contextMessages,
      userMessages,
      [systemMessage],
      configuration,
      tracker,
      options,
    ).pipe(
      map((response) => {
        const content = response.choices[0].message.content;
        const parsed = this.isJsonCodeFenceWrapped(content)
          ? this.tryParseJson<T>(content)
          : JSON.parse(content) as T;
        return parsed;
      }),
      catchError((error) => {
        if (error instanceof SyntaxError) {
          return this.summarize<T>(
            context,
            texts,
            prompt,
            {
              ...configuration,
              max_tokens: configuration.max_tokens
                ? configuration.max_tokens + 100
                : undefined,
              max_completion_tokens: configuration.max_completion_tokens
                ? configuration.max_completion_tokens + 100
                : undefined,
            },
            tracker,
            options,
          );
        }
        return throwError(() => error);
      }),
    );
  }

  request(
    context: OpenAI.GPTMessage[],
    user: OpenAI.GPTMessage[],
    system: OpenAI.GPTMessage[],
    configuration: OpenAI.RequestConfiguration,
    tracker?: OpenAI.RequestTracker,
    options?: OpenAI.RequestOptions,
    attempt?: number,
  ): Observable<OpenAI.CompletionResponse> {
    const messages = [...context, ...user, ...system];
    const body: OpenAI.CompletionRequest = {
      messages,
      ...configuration,
    };

    return this.postCompletionWithTokenLimitRetry(
      this.completionsUrl,
      body,
      context,
      user,
      system,
      configuration,
      tracker,
      options,
      attempt,
      (ctx, limitedUser, sys, cfg, tr, opt, att) =>
        this.request(ctx, limitedUser, sys, cfg, tr, opt, att),
    );
  }

  agentsConversation(
    context: OpenAI.GPTMessage[],
    user: OpenAI.GPTMessage[],
    system: OpenAI.GPTMessage[],
    configuration: OpenAI.RequestConfiguration,
    tracker?: OpenAI.RequestTracker,
    options?: OpenAI.RequestOptions,
    useTools?: string[],
    conversationId?: string,
    attempt?: number,
  ): Observable<OpenAI.ConversationResponse> {
    const messages = [...context, ...user, ...system];
    const body: OpenAI.AgentsConversationRequest = {
      messages,
      ...configuration,
      use_tools: useTools,
      conversation_id: conversationId,
    };

    return this.postCompletionWithTokenLimitRetry(
      this.agentsConversationsUrl,
      body,
      context,
      user,
      system,
      configuration,
      tracker,
      options,
      attempt,
      (ctx, limitedUser, sys, cfg, tr, opt, att) =>
        this.agentsConversation(ctx, limitedUser, sys, cfg, tr, opt, useTools, conversationId, att),
    );
  }

  agentsConversationClearHistory(
    conversationId: string,
    options?: OpenAI.RequestOptions,
  ): Observable<void> {
    const headers = this.getHeaders(
      options?.useGlobalServers ?? false,
      options?.extraHeaders ?? {},
    );
    const body: OpenAI.AgentsConversationClearHistoryRequest = { conversation_id: conversationId };
    return this.http.post<void>(this.agentsConversationsClearHistoryUrl, body, {
      headers,
    });
  }

  private postCompletionWithTokenLimitRetry<TResponse extends OpenAI.CompletionResponse>(
    url: string,
    body: OpenAI.CompletionRequest,
    context: OpenAI.GPTMessage[],
    user: OpenAI.GPTMessage[],
    system: OpenAI.GPTMessage[],
    configuration: OpenAI.RequestConfiguration,
    tracker: OpenAI.RequestTracker | undefined,
    options: OpenAI.RequestOptions | undefined,
    attempt: number | undefined,
    retry: (
      context: OpenAI.GPTMessage[],
      limitedUser: OpenAI.GPTMessage[],
      system: OpenAI.GPTMessage[],
      configuration: OpenAI.RequestConfiguration,
      tracker: OpenAI.RequestTracker | undefined,
      options: OpenAI.RequestOptions | undefined,
      attempt: number | undefined,
    ) => Observable<TResponse>,
  ): Observable<TResponse> {
    let currentAtempt = (attempt ?? 0) + 1;
    const headers = this.getHeaders(
      options?.useGlobalServers ?? false,
      options?.extraHeaders ?? {},
    );
    const messages = [...context, ...user, ...system];

    tracker?.start(url, configuration, messages);

    return this.http
      .post<TResponse>(url, body, {
        headers,
        observe: 'response' as const,
      })
      .pipe(
        tap((response) => {
          const responseBody = response.body;
          if (responseBody) {
            tracker?.end(responseBody);
          }
          options?.onResponseHeaders?.(response.headers);
        }),
        map((response: HttpResponse<TResponse>) => response.body as TResponse),
        catchError((error: Error) => {
          if (error instanceof HttpErrorResponse) {
            const apiError = error.error as OpenAI.ApiError;

            options?.onError?.(apiError, currentAtempt);

            const isContextLengthExceeded =
              error.status === 400 &&
              apiError.error.code === OpenAI.ApiErrorCode.ContextLengthExceeded;

            const isRateLimitExceeded =
              error.status === 429 &&
              apiError.error.message.includes('exceeded token rate limit');

            if (isContextLengthExceeded || isRateLimitExceeded) {
              return this.handleTokenLimit(
                context,
                user,
                system,
                configuration.model as TiktokenModel,
                configuration.max_tokens ??
                  configuration.max_completion_tokens ??
                  0,
              ).pipe(
                switchMap((limitedUser) => {
                  if (limitedUser.length === 0) {
                    tracker?.error(error);
                    return throwError(() => error);
                  }

                  return retry(
                    context,
                    limitedUser,
                    system,
                    configuration,
                    tracker,
                    options,
                    ++currentAtempt,
                  );
                }),
              );
            }
          }

          tracker?.error(error);
          return throwError(() => error);
        }),
      );
  }

  /** Streams chat completions over Server-Sent Events and emits content deltas and usage as they arrive. 
   * Uses fetch to support required headers and POST. */
  streamRequest(
    context: OpenAI.GPTMessage[],
    user: OpenAI.GPTMessage[],
    system: OpenAI.GPTMessage[],
    configuration: OpenAI.RequestConfiguration,
    options?: OpenAI.RequestOptions,
  ): Observable<OpenAI.StreamRequestChunk> {
    const messages = [...context, ...user, ...system];
    const request: OpenAI.CompletionRequest = {
      messages,
      ...configuration,
      stream: true,
      stream_options: {
        include_usage: true,
      },
    };

    return this.streamCompletion(this.completionsUrl, request, options);
  }

  /** Streams an agent conversation over Server-Sent Events, mirroring {@link streamRequest} but
   * targeting the agents endpoint and carrying tool selection and the conversation id. */
  agentsConversationStream(
    context: OpenAI.GPTMessage[],
    user: OpenAI.GPTMessage[],
    system: OpenAI.GPTMessage[],
    configuration: OpenAI.RequestConfiguration,
    options?: OpenAI.RequestOptions,
    useTools?: string[],
    conversationId?: string,
  ): Observable<OpenAI.StreamRequestChunk> {
    const messages = [...context, ...user, ...system];
    const request: OpenAI.AgentsConversationRequest = {
      messages,
      ...configuration,
      use_tools: useTools,
      conversation_id: conversationId,
      stream: true,
      stream_options: {
        include_usage: true,
      },
    };

    return this.streamCompletion(this.agentsConversationsUrl, request, options);
  }

  private streamCompletion(
    url: string,
    request: OpenAI.CompletionRequest,
    options?: OpenAI.RequestOptions,
  ): Observable<OpenAI.StreamRequestChunk> {
    const headers: Record<string, string> = {
      ...this.toHeaderRecord(
        this.getHeaders(
          options?.useGlobalServers ?? false,
          options?.extraHeaders ?? {},
        ),
      ),
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'X-Streaming': 'true',
    };

    return new Observable<OpenAI.StreamRequestChunk>((subscriber) => {
      const controller = new AbortController();

      const consume = async (): Promise<void> => {
        const response = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(request),
          signal: controller.signal,
        });

        // Failure window (a): the request failed before streaming started — a
        // real status code with a buffered body, not an SSE stream.
        const contentType = response.headers.get('Content-Type') ?? '';
        if (!response.ok || !contentType.includes('text/event-stream')) {
          throw (
            this.tryParseApiError(await response.text()) ??
            new Error(`OpenAI stream request failed with status ${response.status}`)
          );
        }
        if (!response.body) {
          throw new Error('OpenAI stream response did not include a body');
        }

        for await (const chunk of parseServerSentEvents(response.body)) {
          // Failure window (b): the generation failed mid-stream.
          const apiError = this.tryParseApiError(chunk);
          if (apiError) {
            throw apiError;
          }

          const completionChunk = chunk as OpenAI.CompletionChunk;
          const content = completionChunk.choices?.[0]?.delta?.content;
          const usage = completionChunk.usage;
          const conversationId = completionChunk.conversation_id;
          if (content || usage || conversationId) {
            subscriber.next({ content, usage, conversationId });
          }
        }
      };

      consume()
        .then(() => subscriber.complete())
        .catch((error: unknown) => {
          // Swallow the AbortError raised by our own teardown; surface the rest.
          if (!controller.signal.aborted) {
            subscriber.error(error);
          }
        });

      return () => controller.abort();
    }).pipe(
      catchError((error: unknown) => {
        // Mirror `request()`: run the error callback, then re-propagate.
        if (this.isApiError(error)) {
          options?.onError?.(error);
        }
        return throwError(() => error);
      }),
    );
  }

  private handleTokenLimit(
    context: OpenAI.GPTMessage[],
    user: OpenAI.GPTMessage[],
    system: OpenAI.GPTMessage[],
    modelName: TiktokenModel,
    maxResponseTokens: number,
  ): Observable<OpenAI.GPTMessage[]> {
    return this.getEncoderForModel(modelName).pipe(
      map((encoder) => {
        const maxTokens = this.maxTokensByModel[modelName];
        if (!maxTokens) {
          throw new Error(`Model ${modelName} not supported`);
        }

        let requestTokens = this.countPromptTokens(encoder, modelName, [
          ...context,
          ...user,
          ...system,
        ]);
        let maxRequestTokens = maxTokens - maxResponseTokens;

        if (requestTokens > maxRequestTokens) {
          for (let index = 0; index < user.length; index++) {
            requestTokens -= this.countPromptTokens(
              encoder,
              modelName,
              [user[index]],
              false,
            );

            if (requestTokens >= maxRequestTokens) {
              user.splice(0, index + 1);
            }
          }
        }

        return user;
      }),
    );
  }

  calculateRequestPromptTokens(
    context: OpenAI.GPTMessage[],
    user: OpenAI.GPTMessage[],
    system: OpenAI.GPTMessage[],
    modelName: TiktokenModel,
  ): Observable<number> {
    return this.getEncoderForModel(modelName).pipe(
      map((encoder) => {
        const maxTokens = this.maxTokensByModel[modelName];
        if (!maxTokens) {
          throw new Error(`Model ${modelName} not supported`);
        }

        let requestTokens = this.countPromptTokens(encoder, modelName, [
          ...context,
          ...user,
          ...system,
        ]);

        return requestTokens;
      }),
    );
  }

  // Based on Python code at https://cookbook.openai.com/examples/how_to_count_tokens_with_tiktoken
  private countPromptTokens(
    encoder: Tiktoken,
    modelName: TiktokenModel,
    messages: OpenAI.GPTMessage[],
    includeReply = true,
  ): number {
    let tokensPerMessage;
    let tokensPerName;

    switch (modelName) {
      case 'gpt-3.5-turbo':
      case 'gpt-4':
      case 'gpt-4o':
      case 'gpt-4o-mini':
      case 'gpt-4.1':
      case 'gpt-4.1-mini':
      case 'gpt-4.1-nano':
        tokensPerMessage = 3;
        tokensPerName = 1;
        break;

      default:
        throw new Error(`Model ${modelName} not supported`);
    }

    let numTokens = 0;
    for (let message of messages) {
      numTokens += tokensPerMessage;
      for (const [key, value] of Object.entries(message)) {
        numTokens += encoder.encode(value).length;
        if (key == 'name') {
          numTokens += tokensPerName;
        }
      }
    }

    if (includeReply) {
      numTokens += tokensPerMessage; // every reply is primed with <|start|>assistant<|message|>
    }

    return numTokens;
  }

  private getEncoderForModel(modelName: TiktokenModel): Observable<Tiktoken> {
    const name = getEncodingNameForModel(modelName);

    return this.http
      .get<TiktokenBPE>(
        `${this.cdn}/assets/tiktoken/ranks/${name}.json?v=${LOAD_UID}`,
      )
      .pipe(
        map((encoding) => new Tiktoken(encoding)),
        catchError(() => {
          throw new Error(`Failed to load encoding for model ${modelName}`);
        }),
      );
  }

  private getHeaders(useGlobalServers: boolean, extra: OpenAI.HeaderMap = {}): HttpHeaders {
    const base: OpenAI.HeaderMap = {
      Authorization: `Bearer ${this.context.token}`,
      ...(useGlobalServers ? { 'X-Deployment-Type': 'global' } : {}),
      ...(this.context.dataCenter ? { 'X-DataCenter-Id': this.context.dataCenter } : {}),
    };
    return new HttpHeaders({ ...base, ...extra });
  }

  private toHeaderRecord(headers: HttpHeaders): Record<string, string> {
    return headers.keys().reduce<Record<string, string>>((record, key) => {
      const value = headers.get(key);
      if (value !== null) {
        record[key] = value;
      }
      return record;
    }, {});
  }

  private isApiError(value: unknown): value is OpenAI.ApiError {
    return (
      typeof value === 'object' &&
      value !== null &&
      !(value instanceof Error) &&
      'error' in value
    );
  }

  private tryParseApiError(raw: unknown): OpenAI.ApiError | undefined {
    let parsed: unknown;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch {
        return undefined;
      }
    } else {
      parsed = raw;
    }
    if (parsed && typeof parsed === 'object' && 'error' in parsed) {
      return parsed as OpenAI.ApiError;
    }
    return undefined;
  }

  tryParseJson<T>(raw: string): T {
    const withoutFences = this.stripCodeFences(raw);
    const extracted = this.extractOuterJsonObject(withoutFences);
    return JSON.parse(extracted) as T;
  }

  private isJsonCodeFenceWrapped(value: string): boolean {
    const trimmed = value.trim();
    const startsWithJsonFence = /^\s*```json\b/i.test(trimmed);
    const endsWithFence = /```\s*$/.test(trimmed);
    return startsWithJsonFence && endsWithFence;
  }

  private stripCodeFences(value: string): string {
    const trimmed = value.trim();
    const withoutStartFence = trimmed.replace(/^\s*```[A-Za-z]*\s*/, '');
    const withoutEndFence = withoutStartFence.replace(/\s*```\s*$/, '');
    return withoutEndFence.trim();
  }

  private extractOuterJsonObject(value: string): string {
    const firstBrace = value.indexOf('{');
    const lastBrace = value.lastIndexOf('}');
    if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) {
      return value;
    }
    return value.substring(firstBrace, lastBrace + 1).trim();
  }
}
